import moment from "moment";
import { ICourseRegistration, SourceOrg, danishMonths } from "./utils";

export interface MonthRevenue {
    month: string;
    monthIndex: number;
    revenue: number;
}

export interface WeekRevenue {
    weekNumber: number;
    startDate: Date;
    endDate: Date;
    revenue: number;
    count: number;
}

export interface WeekRevenueCompare {
    weekNumber: number;
    startDate: Date;
    endDate: Date;
    currentRevenue: number;
    previousRevenue: number;
    prev2Revenue: number;
    currentCount: number;
    previousCount: number;
    prev2Count: number;
}

export interface MonthCount {
    month: string;
    monthIndex: number;
    count: number;
}

export interface CourseRevenue {
    title: string;
    revenue: number;
}

export interface CourseCount {
    title: string;
    count: number;
}

export interface SourceCount {
    source: string;
    count: number;
}

export interface SourceCourseBreakdown {
    title: string;
    count: number;
    revenue: number;
}

export interface SourceBreakdown {
    source: string;
    count: number;
    revenue: number;
    courses: SourceCourseBreakdown[];
}

export interface CategoryRevenue {
    category: string;
    revenue: number;
    courses?: string[];
}

export interface BestMonthData {
    month: string;
    monthIndex: number;
    count: number;
    revenue: number;
}

export interface DayCount {
    day: string;
    dayIndex: number;
    count: number;
}

export interface HourCount {
    hour: string;
    hourIndex: number;
    count: number;
}

const COURSE_CATEGORIES: [string, string][] = [
    ["Power BI", "Power BI"],
    ["Power Automate", "Power Automate"],
    ["Power Apps", "Power Apps"],
    ["Power Platform", "Power Platform"],
    ["Excel", "Excel"],
    ["SQL", "SQL"],
    ["Azure", "Azure"],
    ["Python", "Python"],
    ["Microsoft 365", "Microsoft 365"],
    ["Teams", "Teams"],
    ["SharePoint", "SharePoint"],
    ["Word", "Word"],
    ["Outlook", "Outlook"],
    ["Project", "Project"],
];

const DANISH_DAYS = ["søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag"];

function parsePrice(reg: ICourseRegistration, firm: string): number {
    let raw: string | number;
    if (firm === "Alle") {
        raw = reg.totalPrice || reg.price;
    } else {
        raw = firm === "4D" ? reg.totalPrice : reg.price;
    }
    if (!raw) return 0;
    const str = typeof raw === "string" ? raw.replace(/\./g, "").replace(",", ".") : raw;
    const num = typeof str === "number" ? str : parseFloat(str);
    return isNaN(num) ? 0 : num;
}

function parseParticipants(reg: ICourseRegistration): number {
    const raw = reg.participants;
    if (!raw || raw === "-") return 1;
    const num = typeof raw === "number" ? raw : parseInt(String(raw));
    return isNaN(num) || num <= 0 ? 1 : num;
}

export function truncateTitle(title: string, maxLen: number = 35): string {
    return title.length > maxLen ? title.substring(0, maxLen) + "..." : title;
}

export function normalizeSource(source: string): string {
    const trimmed = source.trim();
    const lower = trimmed.toLowerCase();
    if (
        lower === "anbefalet af kollega" ||
        lower === "anbefalet af bekendt" ||
        lower === "anbefalet af kollega/bekendt" ||
        lower === "anbefalet af bekendt/kollega"
    ) {
        return "Anbefalet af kollega/bekendt";
    }
    if (lower === "andet" || lower === "andre" || lower === "anden" || lower === "other") {
        return "Andet";
    }
    return trimmed;
}

export function formatIndexRatio(current: number, previous: number): string | null {
    if (!previous || previous <= 0) return null;
    const ratio = current / previous;
    return ratio.toFixed(2).replace(".", ",");
}

function getCourseCategory(title: string): string {
    const lower = title.toLowerCase();
    for (const [keyword, category] of COURSE_CATEGORIES) {
        if (lower.includes(keyword.toLowerCase())) return category;
    }
    return "Andet";
}

export function aggregateRevenueByWeek(
    registrations: ICourseRegistration[],
    firm: string,
    periodStart: Date,
    periodEnd: Date,
    useCourseDate: boolean
): WeekRevenue[] {
    const buckets: Record<string, WeekRevenue> = {};
    const startMs = periodStart.getTime();
    const endMs = periodEnd.getTime();

    for (const reg of registrations) {
        const basis = useCourseDate ? reg.startDate : reg.registrationDate;
        if (!basis) continue;
        const d = basis instanceof Date ? basis : new Date(basis);
        const t = d?.getTime();
        if (!t || isNaN(t)) continue;
        if (t < startMs || t > endMs) continue;

        const m = moment(d);
        const weekNumber = m.isoWeek();
        const isoYear = m.isoWeekYear();
        const key = `${isoYear}-W${weekNumber}`;

        if (!buckets[key]) {
            const monday = m.clone().startOf("isoWeek").toDate();
            const sunday = m.clone().endOf("isoWeek").toDate();
            const clampStart = monday.getTime() < startMs ? new Date(startMs) : monday;
            const clampEnd = sunday.getTime() > endMs ? new Date(endMs) : sunday;
            buckets[key] = { weekNumber, startDate: clampStart, endDate: clampEnd, revenue: 0, count: 0 };
        }
        buckets[key].revenue += parsePrice(reg, firm);
        buckets[key].count++;
    }

    return Object.values(buckets)
        .map(b => ({ ...b, revenue: Math.round(b.revenue) }))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}

export function aggregateRevenueByMonth(registrations: ICourseRegistration[], firm: string): MonthRevenue[] {
    const map: Record<number, number> = {};

    for (const reg of registrations) {
        const monthIdx = parseInt(reg.month);
        if (isNaN(monthIdx)) continue;
        if (!map[monthIdx]) map[monthIdx] = 0;
        map[monthIdx] += parsePrice(reg, firm);
    }

    return Object.entries(map)
        .map(([idx, revenue]) => ({
            month: danishMonths[parseInt(idx)] ?? idx,
            monthIndex: parseInt(idx),
            revenue: Math.round(revenue),
        }))
        .sort((a, b) => a.monthIndex - b.monthIndex);
}

export function aggregateCountByMonth(registrations: ICourseRegistration[]): MonthCount[] {
    const map: Record<number, number> = {};

    for (const reg of registrations) {
        const monthIdx = parseInt(reg.month);
        if (isNaN(monthIdx)) continue;
        if (!map[monthIdx]) map[monthIdx] = 0;
        map[monthIdx]++;
    }

    return Object.entries(map)
        .map(([idx, count]) => ({
            month: danishMonths[parseInt(idx)] ?? idx,
            monthIndex: parseInt(idx),
            count,
        }))
        .sort((a, b) => a.monthIndex - b.monthIndex);
}

export function topCoursesByRevenue(registrations: ICourseRegistration[], firm: string, n: number = 10): CourseRevenue[] {
    const map: Record<string, number> = {};

    for (const reg of registrations) {
        const title = reg.title?.trim();
        if (!title) continue;
        if (!map[title]) map[title] = 0;
        map[title] += parsePrice(reg, firm);
    }

    return Object.entries(map)
        .map(([title, revenue]) => ({ title, revenue: Math.round(revenue) }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, n);
}

export function topCoursesByCount(registrations: ICourseRegistration[], n: number = 10): CourseCount[] {
    const map: Record<string, number> = {};

    for (const reg of registrations) {
        const title = reg.title?.trim();
        if (!title) continue;
        if (!map[title]) map[title] = 0;
        map[title]++;
    }

    return Object.entries(map)
        .map(([title, count]) => ({ title, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, n);
}

export function aggregateSourceBreakdown(registrations: ICourseRegistration[], firm: string): SourceBreakdown[] {
    const sourceMap: Record<string, { count: number; revenue: number; courseMap: Record<string, { count: number; revenue: number }> }> = {};

    for (const reg of registrations) {
        const rawSource = reg.heardFrom?.trim() || "-";
        if (rawSource === "-" || rawSource.match(/^[-–—.]+$/)) continue;
        const source = normalizeSource(rawSource);
        const title = reg.title?.trim();
        if (!title) continue;
        const price = parsePrice(reg, firm);

        if (!sourceMap[source]) {
            sourceMap[source] = { count: 0, revenue: 0, courseMap: {} };
        }
        sourceMap[source].count++;
        sourceMap[source].revenue += price;

        if (!sourceMap[source].courseMap[title]) {
            sourceMap[source].courseMap[title] = { count: 0, revenue: 0 };
        }
        sourceMap[source].courseMap[title].count++;
        sourceMap[source].courseMap[title].revenue += price;
    }

    return Object.entries(sourceMap)
        .map(([source, entry]) => ({
            source,
            count: entry.count,
            revenue: Math.round(entry.revenue),
            courses: Object.entries(entry.courseMap)
                .map(([title, c]) => ({ title, count: c.count, revenue: Math.round(c.revenue) }))
                .sort((a, b) => b.revenue - a.revenue),
        }))
        .sort((a, b) => b.count - a.count);
}

export function aggregateBySource(registrations: ICourseRegistration[]): SourceCount[] {
    const map: Record<string, number> = {};

    for (const reg of registrations) {
        const rawSource = reg.heardFrom?.trim() || "-";
        if (rawSource === "-" || rawSource.match(/^[-–—.]+$/)) continue;
        const source = normalizeSource(rawSource);
        if (!map[source]) map[source] = 0;
        map[source]++;
    }

    return Object.entries(map)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count);
}

export function aggregateRevenueByCategory(registrations: ICourseRegistration[], firm: string): CategoryRevenue[] {
    const map: Record<string, number> = {};
    const coursesInCategory: Record<string, Set<string>> = {};

    for (const reg of registrations) {
        const title = reg.title?.trim();
        if (!title) continue;
        const category = getCourseCategory(title);
        if (!map[category]) {
            map[category] = 0;
            coursesInCategory[category] = new Set();
        }
        map[category] += parsePrice(reg, firm);
        coursesInCategory[category].add(title);
    }

    return Object.entries(map)
        .map(([category, revenue]) => ({
            category,
            revenue: Math.round(revenue),
            courses: Array.from(coursesInCategory[category] || []),
        }))
        .sort((a, b) => b.revenue - a.revenue);
}

export function bestMonths(registrations: ICourseRegistration[], firm: string): BestMonthData[] {
    const countMap: Record<number, number> = {};
    const revenueMap: Record<number, number> = {};

    for (const reg of registrations) {
        const monthIdx = parseInt(reg.month);
        if (isNaN(monthIdx)) continue;
        if (!countMap[monthIdx]) {
            countMap[monthIdx] = 0;
            revenueMap[monthIdx] = 0;
        }
        countMap[monthIdx]++;
        revenueMap[monthIdx] += parsePrice(reg, firm);
    }

    return Object.entries(countMap)
        .map(([idx, count]) => ({
            month: danishMonths[parseInt(idx)] ?? idx,
            monthIndex: parseInt(idx),
            count,
            revenue: Math.round(revenueMap[parseInt(idx)] || 0),
        }))
        .sort((a, b) => b.count - a.count);
}

export function aggregateByDayOfWeek(registrations: ICourseRegistration[]): DayCount[] {
    const map: Record<number, number> = {};

    for (const reg of registrations) {
        if (!reg.registrationDate) continue;
        const day = new Date(reg.registrationDate).getDay();
        if (!map[day]) map[day] = 0;
        map[day]++;
    }

    // Sort Monday first (1,2,3,4,5,6,0)
    const mondayFirst = [1, 2, 3, 4, 5, 6, 0];
    return mondayFirst
        .filter((d) => map[d] !== undefined)
        .map((d) => ({
            day: DANISH_DAYS[d],
            dayIndex: d,
            count: map[d],
        }));
}

export function aggregateByHour(registrations: ICourseRegistration[]): HourCount[] {
    const map: Record<number, number> = {};

    for (const reg of registrations) {
        if (!reg.registrationDate) continue;
        const hour = new Date(reg.registrationDate).getHours();
        if (!map[hour]) map[hour] = 0;
        map[hour]++;
    }

    return Object.entries(map)
        .map(([h, count]) => ({
            hour: parseInt(h).toString().padStart(2, "0") + ":00",
            hourIndex: parseInt(h),
            count,
        }))
        .sort((a, b) => a.hourIndex - b.hourIndex);
}

export function computeRepeatRate(registrations: ICourseRegistration[]): { totalUnique: number; returning: number; repeatRate: number } {
    const emailMap: Record<string, number> = {};

    for (const reg of registrations) {
        const email = reg.contactPersonEmail?.trim()?.toLowerCase();
        if (!email || email === "-") continue;
        emailMap[email] = (emailMap[email] || 0) + 1;
    }

    const totalUnique = Object.keys(emailMap).length;
    const returning = Object.values(emailMap).filter((c) => c > 1).length;
    const repeatRate = totalUnique > 0 ? Math.round((returning / totalUnique) * 100) : 0;

    return { totalUnique, returning, repeatRate };
}

export interface MonthRevenueCompare { month: string; monthIndex: number; current: number; previous: number; }
export interface MonthCountCompare { month: string; monthIndex: number; current: number; previous: number; }
export interface DayCountCompare { day: string; dayIndex: number; current: number; previous: number; }
export interface HourCountCompare { hour: string; hourIndex: number; current: number; previous: number; }
export interface BestMonthCompare {
    month: string; monthIndex: number;
    count: number; revenue: number;
    prevCount: number; prevRevenue: number;
}
export interface CourseRevenueCompare { title: string; current: number; previous: number; }
export interface CourseCountCompare { title: string; current: number; previous: number; }
export interface CategoryRevenueCompare { category: string; current: number; previous: number; courses?: string[]; }
export interface SourceCountCompare { source: string; current: number; previous: number; }
export interface SourceCourseCompare {
    title: string;
    currentCount: number;
    previousCount: number;
    currentRevenue: number;
    previousRevenue: number;
}
export interface SourceBreakdownCompare {
    source: string;
    currentCount: number;
    previousCount: number;
    currentRevenue: number;
    previousRevenue: number;
    courses: SourceCourseCompare[];
}

export function mergeRevenueByMonth(current: MonthRevenue[], previous: MonthRevenue[]): MonthRevenueCompare[] {
    const keys = new Set<number>();
    current.forEach(r => keys.add(r.monthIndex));
    previous.forEach(r => keys.add(r.monthIndex));
    const out: MonthRevenueCompare[] = [];
    Array.from(keys).sort((a, b) => a - b).forEach(idx => {
        out.push({
            month: danishMonths[idx] ?? String(idx),
            monthIndex: idx,
            current: current.find(r => r.monthIndex === idx)?.revenue ?? 0,
            previous: previous.find(r => r.monthIndex === idx)?.revenue ?? 0,
        });
    });
    return out;
}

// Joins current-, prev-, and prev2-year weekly buckets on ISO week number. Rows
// ordered by whichever series has data for that week (prefers current → prev → prev2)
// so chronological sort within the display period is preserved.
export function mergeRevenueByWeek(
    current: WeekRevenue[],
    previous: WeekRevenue[],
    prev2: WeekRevenue[] = [],
): WeekRevenueCompare[] {
    const currByWeek = new Map<number, WeekRevenue>();
    current.forEach(w => currByWeek.set(w.weekNumber, w));
    const prevByWeek = new Map<number, WeekRevenue>();
    previous.forEach(w => prevByWeek.set(w.weekNumber, w));
    const prev2ByWeek = new Map<number, WeekRevenue>();
    prev2.forEach(w => prev2ByWeek.set(w.weekNumber, w));

    const weeks = new Set<number>();
    current.forEach(w => weeks.add(w.weekNumber));
    previous.forEach(w => weeks.add(w.weekNumber));
    prev2.forEach(w => weeks.add(w.weekNumber));

    const out: WeekRevenueCompare[] = [];
    weeks.forEach(wk => {
        const c = currByWeek.get(wk);
        const p = prevByWeek.get(wk);
        const p2 = prev2ByWeek.get(wk);
        const ref = c ? c : (p ? p : p2);
        if (!ref) return;
        out.push({
            weekNumber: wk,
            startDate: ref.startDate,
            endDate: ref.endDate,
            currentRevenue: c?.revenue ?? 0,
            previousRevenue: p?.revenue ?? 0,
            prev2Revenue: p2?.revenue ?? 0,
            currentCount: c?.count ?? 0,
            previousCount: p?.count ?? 0,
            prev2Count: p2?.count ?? 0,
        });
    });

    return out.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}

export function mergeCountByMonth(current: MonthCount[], previous: MonthCount[]): MonthCountCompare[] {
    const keys = new Set<number>();
    current.forEach(r => keys.add(r.monthIndex));
    previous.forEach(r => keys.add(r.monthIndex));
    const out: MonthCountCompare[] = [];
    Array.from(keys).sort((a, b) => a - b).forEach(idx => {
        out.push({
            month: danishMonths[idx] ?? String(idx),
            monthIndex: idx,
            current: current.find(r => r.monthIndex === idx)?.count ?? 0,
            previous: previous.find(r => r.monthIndex === idx)?.count ?? 0,
        });
    });
    return out;
}

export function mergeByDayOfWeek(current: DayCount[], previous: DayCount[]): DayCountCompare[] {
    const mondayFirst = [1, 2, 3, 4, 5, 6, 0];
    const out: DayCountCompare[] = [];
    mondayFirst.forEach(d => {
        const c = current.find(x => x.dayIndex === d)?.count ?? 0;
        const p = previous.find(x => x.dayIndex === d)?.count ?? 0;
        if (c === 0 && p === 0) return;
        out.push({ day: DANISH_DAYS[d], dayIndex: d, current: c, previous: p });
    });
    return out;
}

export function mergeByHour(current: HourCount[], previous: HourCount[]): HourCountCompare[] {
    const hoursWithData: number[] = [];
    current.forEach(r => hoursWithData.push(r.hourIndex));
    previous.forEach(r => hoursWithData.push(r.hourIndex));
    if (hoursWithData.length === 0) return [];

    const min = Math.min(...hoursWithData);
    const max = Math.max(...hoursWithData);
    const out: HourCountCompare[] = [];
    for (let h = min; h <= max; h++) {
        out.push({
            hour: h.toString().padStart(2, "0") + ":00",
            hourIndex: h,
            current: current.find(r => r.hourIndex === h)?.count ?? 0,
            previous: previous.find(r => r.hourIndex === h)?.count ?? 0,
        });
    }
    return out;
}

export function mergeBestMonths(current: BestMonthData[], previous: BestMonthData[]): BestMonthCompare[] {
    const keys = new Set<number>();
    current.forEach(r => keys.add(r.monthIndex));
    previous.forEach(r => keys.add(r.monthIndex));
    const out: BestMonthCompare[] = [];
    keys.forEach(idx => {
        const c = current.find(r => r.monthIndex === idx);
        const p = previous.find(r => r.monthIndex === idx);
        out.push({
            month: danishMonths[idx] ?? String(idx),
            monthIndex: idx,
            count: c?.count ?? 0,
            revenue: c?.revenue ?? 0,
            prevCount: p?.count ?? 0,
            prevRevenue: p?.revenue ?? 0,
        });
    });
    return out.sort((a, b) => (b.count + b.prevCount) - (a.count + a.prevCount));
}

export function mergeTopCoursesRevenue(current: CourseRevenue[], previous: CourseRevenue[]): CourseRevenueCompare[] {
    const prevMap = new Map(previous.map(r => [r.title, r.revenue]));
    return current.map(r => ({
        title: r.title,
        current: r.revenue,
        previous: prevMap.get(r.title) ?? 0,
    }));
}

export function mergeTopCoursesCount(current: CourseCount[], previous: CourseCount[]): CourseCountCompare[] {
    const prevMap = new Map(previous.map(r => [r.title, r.count]));
    return current.map(r => ({
        title: r.title,
        current: r.count,
        previous: prevMap.get(r.title) ?? 0,
    }));
}

export function mergeCategoryRevenue(current: CategoryRevenue[], previous: CategoryRevenue[]): CategoryRevenueCompare[] {
    const keys = new Set<string>();
    current.forEach(r => keys.add(r.category));
    previous.forEach(r => keys.add(r.category));
    const out: CategoryRevenueCompare[] = [];
    keys.forEach(cat => {
        const c = current.find(r => r.category === cat);
        const p = previous.find(r => r.category === cat);
        out.push({
            category: cat,
            current: c?.revenue ?? 0,
            previous: p?.revenue ?? 0,
            courses: c?.courses ?? [],
        });
    });
    return out.sort((a, b) => b.current - a.current);
}

export function mergeSourceBreakdown(current: SourceBreakdown[], previous: SourceBreakdown[]): SourceBreakdownCompare[] {
    const sources = new Set<string>();
    current.forEach(s => sources.add(s.source));
    previous.forEach(s => sources.add(s.source));

    const out: SourceBreakdownCompare[] = [];
    sources.forEach(src => {
        const cur = current.find(s => s.source === src);
        const prev = previous.find(s => s.source === src);

        const titles = new Set<string>();
        cur?.courses.forEach(c => titles.add(c.title));
        prev?.courses.forEach(c => titles.add(c.title));

        const courses: SourceCourseCompare[] = [];
        titles.forEach(title => {
            const c = cur?.courses.find(x => x.title === title);
            const p = prev?.courses.find(x => x.title === title);
            courses.push({
                title,
                currentCount: c?.count ?? 0,
                previousCount: p?.count ?? 0,
                currentRevenue: c?.revenue ?? 0,
                previousRevenue: p?.revenue ?? 0,
            });
        });
        courses.sort((a, b) => b.currentRevenue - a.currentRevenue || b.previousRevenue - a.previousRevenue);

        out.push({
            source: src,
            currentCount: cur?.count ?? 0,
            previousCount: prev?.count ?? 0,
            currentRevenue: cur?.revenue ?? 0,
            previousRevenue: prev?.revenue ?? 0,
            courses,
        });
    });

    return out.sort((a, b) => b.currentCount - a.currentCount);
}

export function mergeBySource(current: SourceCount[], previous: SourceCount[]): SourceCountCompare[] {
    const keys = new Set<string>();
    current.forEach(r => keys.add(r.source));
    previous.forEach(r => keys.add(r.source));
    const out: SourceCountCompare[] = [];
    keys.forEach(src => {
        out.push({
            source: src,
            current: current.find(r => r.source === src)?.count ?? 0,
            previous: previous.find(r => r.source === src)?.count ?? 0,
        });
    });
    return out.sort((a, b) => b.current - a.current);
}

export interface KpiComparison {
    current: number;
    previous: number;
    index: number | null;
    deltaPct: number | null;
    deltaAbs: number;
}

export function compareKpi(current: number, previous: number): KpiComparison {
    if (!previous || previous === 0) {
        return {
            current,
            previous,
            index: null,
            deltaPct: null,
            deltaAbs: current,
        };
    }
    const index = Math.round((current / previous) * 100);
    const deltaPct = Math.round(((current - previous) / previous) * 1000) / 10;
    return {
        current,
        previous,
        index,
        deltaPct,
        deltaAbs: current - previous,
    };
}

export function computeKpis(registrations: ICourseRegistration[], firm: string) {
    let totalRevenue = 0;
    let totalParticipants = 0;
    const courseCountMap: Record<string, number> = {};

    for (const reg of registrations) {
        totalRevenue += parsePrice(reg, firm);
        totalParticipants += parseParticipants(reg);
        const title = reg.title?.trim();
        if (title) {
            courseCountMap[title] = (courseCountMap[title] || 0) + 1;
        }
    }

    const totalRegistrations = registrations.length;
    const avgRevenue = totalRegistrations > 0 ? Math.round(totalRevenue / totalRegistrations) : 0;
    const avgParticipants = totalRegistrations > 0 ? Math.round((totalParticipants / totalRegistrations) * 10) / 10 : 0;

    let topCourse = "-";
    let topCourseCount = 0;
    for (const [title, count] of Object.entries(courseCountMap)) {
        if (count > topCourseCount) {
            topCourse = title;
            topCourseCount = count;
        }
    }

    return {
        totalRevenue: Math.round(totalRevenue),
        totalRegistrations,
        avgRevenue,
        avgParticipants,
        topCourse,
        topCourseCount,
    };
}

// ------------------------------------------------------------------
// Year-Month aggregations (used by forecast.ts)
//
// These preserve (year, monthIndex) keys so a multi-year window
// produces a proper time series. Contrast aggregateRevenueByMonth
// above, which collapses all years into the same 12-bucket month
// histogram.
//
// Output is always SORTED ASCENDING by (year, monthIndex) and DENSE:
// a bucket with zero registrations is present with revenue/count 0.
// A dense series is what seasonal forecasting methods require.
// ------------------------------------------------------------------

export interface YearMonthPoint {
    year: number;
    monthIndex: number;   // 0-11
    key: string;          // "YYYY-MM"
    value: number;
}

function ymKey(year: number, monthIndex: number): string {
    return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
}

function basisDate(reg: ICourseRegistration): Date | null {
    const d = reg.registrationDate instanceof Date
        ? reg.registrationDate
        : reg.registrationDate ? new Date(reg.registrationDate) : null;
    if (!d) return null;
    const t = d.getTime();
    return (!t || isNaN(t)) ? null : d;
}

function denseYearMonthRange(startYear: number, startMonth: number, endYear: number, endMonth: number): { year: number; monthIndex: number }[] {
    const out: { year: number; monthIndex: number }[] = [];
    let y = startYear, m = startMonth;
    while (y < endYear || (y === endYear && m <= endMonth)) {
        out.push({ year: y, monthIndex: m });
        m++;
        if (m > 11) { m = 0; y++; }
    }
    return out;
}

function fillDenseSeries(rawByKey: Record<string, number>, series: { year: number; monthIndex: number }[]): YearMonthPoint[] {
    return series.map(({ year, monthIndex }) => {
        const key = ymKey(year, monthIndex);
        const raw = rawByKey[key] ?? 0;
        return { year, monthIndex, key, value: Math.round(raw) };
    });
}

function boundsFromRegs(registrations: ICourseRegistration[]): { startYear: number; startMonth: number; endYear: number; endMonth: number } | null {
    let minT = Infinity, maxT = -Infinity;
    for (const reg of registrations) {
        const d = basisDate(reg);
        if (!d) continue;
        const t = d.getTime();
        if (t < minT) minT = t;
        if (t > maxT) maxT = t;
    }
    if (!isFinite(minT) || !isFinite(maxT)) return null;
    const min = new Date(minT);
    const max = new Date(maxT);
    return {
        startYear: min.getFullYear(),
        startMonth: min.getMonth(),
        endYear: max.getFullYear(),
        endMonth: max.getMonth(),
    };
}

/**
 * Dense monthly revenue series across all years present in `registrations`.
 * firm: "Alle" | "4D" | "PeopleNet" — reuses existing parsePrice logic.
 */
export function aggregateRevenueByYearMonth(registrations: ICourseRegistration[], firm: string): YearMonthPoint[] {
    const bounds = boundsFromRegs(registrations);
    if (!bounds) return [];

    const rawByKey: Record<string, number> = {};
    for (const reg of registrations) {
        const d = basisDate(reg);
        if (!d) continue;
        const key = ymKey(d.getFullYear(), d.getMonth());
        rawByKey[key] = (rawByKey[key] || 0) + parsePrice(reg, firm);
    }

    const series = denseYearMonthRange(bounds.startYear, bounds.startMonth, bounds.endYear, bounds.endMonth);
    return fillDenseSeries(rawByKey, series);
}

/** Dense monthly registration count series. */
export function aggregateCountByYearMonth(registrations: ICourseRegistration[]): YearMonthPoint[] {
    const bounds = boundsFromRegs(registrations);
    if (!bounds) return [];

    const rawByKey: Record<string, number> = {};
    for (const reg of registrations) {
        const d = basisDate(reg);
        if (!d) continue;
        const key = ymKey(d.getFullYear(), d.getMonth());
        rawByKey[key] = (rawByKey[key] || 0) + 1;
    }

    const series = denseYearMonthRange(bounds.startYear, bounds.startMonth, bounds.endYear, bounds.endMonth);
    return fillDenseSeries(rawByKey, series);
}

/**
 * Per-category year-month revenue series.
 * Returns ONE series per category, only for categories with >= minCompleteYears
 * of history (2 by default) per the forecast plan's new-category policy.
 * The per-category filter is evaluated using bounds of the FULL input,
 * so a category that only appears in the last 6 months is excluded.
 */
export function aggregateRevenueByCategoryYearMonth(
    registrations: ICourseRegistration[],
    firm: string,
    minCompleteYears: number = 2,
): { category: string; series: YearMonthPoint[] }[] {
    const bounds = boundsFromRegs(registrations);
    if (!bounds) return [];

    // Bucket by (category, ym).
    const byCat: Record<string, Record<string, number>> = {};
    const firstSeenByCat: Record<string, number> = {};

    for (const reg of registrations) {
        const title = reg.title?.trim();
        if (!title) continue;
        const d = basisDate(reg);
        if (!d) continue;
        const cat = getCourseCategory(title);
        const key = ymKey(d.getFullYear(), d.getMonth());
        const t = d.getTime();

        if (!byCat[cat]) { byCat[cat] = {}; firstSeenByCat[cat] = t; }
        if (t < firstSeenByCat[cat]) firstSeenByCat[cat] = t;
        byCat[cat][key] = (byCat[cat][key] || 0) + parsePrice(reg, firm);
    }

    const series = denseYearMonthRange(bounds.startYear, bounds.startMonth, bounds.endYear, bounds.endMonth);

    // Category-history minimum: from its first sighting to the latest bucket, in months.
    const totalMonths = series.length;
    const minMonths = minCompleteYears * 12;

    return Object.keys(byCat)
        .filter(cat => {
            const first = new Date(firstSeenByCat[cat]);
            const firstIdx = series.findIndex(p => p.year === first.getFullYear() && p.monthIndex === first.getMonth());
            if (firstIdx < 0) return false;
            return (totalMonths - firstIdx) >= minMonths;
        })
        .map(cat => ({
            category: cat,
            series: fillDenseSeries(byCat[cat], series),
        }))
        .sort((a, b) => {
            const totalA = a.series.reduce((s, p) => s + p.value, 0);
            const totalB = b.series.reduce((s, p) => s + p.value, 0);
            return totalB - totalA;
        });
}

/**
 * Per-firm year-month series for a given valueKind.
 *
 * Uses reg.sourceOrg to split 4D vs PeopleNet. Rows without sourceOrg
 * (legacy objects constructed elsewhere) are bucketed under "Ukendt"
 * but only included if their series has enough data.
 */
export function aggregateByFirmYearMonth(
    registrations: ICourseRegistration[],
    valueKind: "revenue" | "count",
): { firm: SourceOrg | "Ukendt"; series: YearMonthPoint[] }[] {
    const bounds = boundsFromRegs(registrations);
    if (!bounds) return [];

    const byFirm: Record<string, Record<string, number>> = {};

    for (const reg of registrations) {
        const d = basisDate(reg);
        if (!d) continue;
        const firm: string = reg.sourceOrg ? reg.sourceOrg : "Ukendt";
        const key = ymKey(d.getFullYear(), d.getMonth());
        if (!byFirm[firm]) byFirm[firm] = {};
        if (valueKind === "revenue") {
            // For per-firm breakdown we always use the firm-correct price
            // (4D totalPrice, PeopleNet price). Passing "Alle" to parsePrice
            // falls back to totalPrice || price, which matches what 4D rows use
            // and gives PeopleNet rows their price when totalPrice is null.
            byFirm[firm][key] = (byFirm[firm][key] || 0) + parsePrice(reg, firm === "Ukendt" ? "Alle" : firm);
        } else {
            byFirm[firm][key] = (byFirm[firm][key] || 0) + 1;
        }
    }

    const series = denseYearMonthRange(bounds.startYear, bounds.startMonth, bounds.endYear, bounds.endMonth);

    return Object.keys(byFirm)
        .filter(firm => firm === "4D" || firm === "PeopleNet")  // drop "Ukendt" from output
        .map(firm => {
            const typedFirm: SourceOrg = firm === "4D" ? "4D" : "PeopleNet";
            return {
                firm: typedFirm,
                series: fillDenseSeries(byFirm[firm], series),
            };
        })
        .sort((a, b) => a.firm.localeCompare(b.firm));
}

/**
 * Hard cutoff: clip series to the last day of the last COMPLETE month relative to `asOf`.
 * A month is complete once `asOf` is past the end of that month. This drops partial
 * current-month data before training the forecast (avoids training on e.g. only 22 days
 * of April 2026).
 */
export function clipToLastCompleteMonth(series: YearMonthPoint[], asOf: Date = new Date()): YearMonthPoint[] {
    if (series.length === 0) return series;
    // Last complete month = previous month of asOf.
    const asOfY = asOf.getFullYear();
    const asOfM = asOf.getMonth();
    // If we're on any day of month M, only months 0..M-1 of current year
    // (and every month of prior years) are complete.
    let lastY = asOfY, lastM = asOfM - 1;
    if (lastM < 0) { lastM = 11; lastY--; }
    return series.filter(p => (p.year < lastY) || (p.year === lastY && p.monthIndex <= lastM));
}

/** Count unique (year, monthIndex) buckets present in the series. Must be DENSE for this to be useful. */
export function completeMonthCount(series: YearMonthPoint[]): number {
    return series.length;
}

